import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Mail, FileText, Send, Loader2 } from 'lucide-react';

export function EmailPromptDialog({
  open, onOpenChange,
  dispatch,
  onYes, onNo,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Email Dispatch Note?
          </DialogTitle>
          <DialogDescription>
            {dispatch?.dispatch_number 
              ? `Dispatch ${dispatch.dispatch_number} for ${dispatch.customer_name || 'customer'} has been marked as dispatched.`
              : 'Order has been dispatched.'
            }
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground py-2">
          Would you like to email the dispatch note to the customer?
        </p>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onNo} data-testid="btn-email-prompt-no">
            No, Skip
          </Button>
          <Button onClick={onYes} data-testid="btn-email-prompt-yes">
            <Mail className="w-4 h-4 mr-2" /> Yes, Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EmailComposeDialog({
  open, onOpenChange,
  dispatch,
  emailData, setEmailData,
  ccInput, setCcInput,
  onAddCc, onRemoveCc,
  onSend, sending,
  onCancel,
  docType = 'dispatch_note',  // 'dispatch_note' | 'coa' | 'pallet_label'
  suggestedRecipients = [],   // [{email, is_primary, count_for_doc, last_used_for_doc}]
}) {
  const DOC_META = {
    dispatch_note: { title: 'Send Dispatch Note via Email', label: 'Dispatch Note', icon: <FileText className="w-4 h-4 text-muted-foreground" /> },
    coa: { title: 'Send Certificate of Analysis via Email', label: 'Certificate of Analysis Conformance', icon: <FileText className="w-4 h-4 text-emerald-600" /> },
    pallet_label: { title: 'Send Pallet Label via Email', label: 'Pallet Label(s)', icon: <FileText className="w-4 h-4 text-blue-600" /> },
  };
  const meta = DOC_META[docType] || DOC_META.dispatch_note;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {meta.title}
          </DialogTitle>
          <DialogDescription>
            Send {meta.label} for dispatch #{dispatch?.dispatch_number} with PDF attachment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>To *</Label>
            <Input
              type="email"
              value={emailData.to_email}
              onChange={(e) => setEmailData(prev => ({ ...prev, to_email: e.target.value }))}
              placeholder="customer@example.com"
              data-testid="input-dispatch-email-to"
            />
            {suggestedRecipients && suggestedRecipients.length > 0 && (
              <div className="space-y-1" data-testid="suggested-recipients">
                <p className="text-xs text-muted-foreground">
                  Recent recipients for {DOC_META[docType]?.label || 'this customer'}:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestedRecipients.slice(0, 8).map((r) => {
                    const isInTo = emailData.to_email?.toLowerCase() === r.email.toLowerCase();
                    const isInCc = (emailData.cc_emails || []).some(c => c.toLowerCase() === r.email.toLowerCase());
                    return (
                      <div key={r.email} className="inline-flex items-center gap-0.5 group">
                        <button
                          type="button"
                          onClick={() => setEmailData(prev => ({ ...prev, to_email: r.email }))}
                          className={`text-xs px-2 py-0.5 rounded-l border transition-colors ${
                            isInTo
                              ? 'bg-blue-100 border-blue-300 text-blue-800'
                              : r.is_primary
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100'
                                : 'bg-zinc-100 border-zinc-200 hover:bg-zinc-200'
                          }`}
                          title={r.is_primary ? 'Primary customer contact' : r.count_for_doc > 0 ? `Used ${r.count_for_doc}× for this document type` : 'Used for other document types'}
                          data-testid={`chip-recipient-${r.email}`}
                        >
                          {r.is_primary && <span className="mr-1">★</span>}
                          {r.email}
                          {r.count_for_doc > 0 && (
                            <span className="ml-1 text-[10px] opacity-70">×{r.count_for_doc}</span>
                          )}
                        </button>
                        {!isInCc && !isInTo && (
                          <button
                            type="button"
                            onClick={() => {
                              if (!(emailData.cc_emails || []).includes(r.email)) {
                                setEmailData(prev => ({ ...prev, cc_emails: [...(prev.cc_emails || []), r.email] }));
                              }
                            }}
                            className="text-[10px] px-1 py-0.5 rounded-r border border-l-0 bg-white hover:bg-zinc-100"
                            title="Add to CC"
                            data-testid={`chip-cc-${r.email}`}
                          >
                            +cc
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label>CC (optional)</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                placeholder="Add CC email..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAddCc())}
              />
              <Button type="button" variant="outline" onClick={onAddCc}>Add</Button>
            </div>
            {emailData.cc_emails.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {emailData.cc_emails.map((email, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {email}
                    <button type="button" onClick={() => onRemoveCc(email)} className="ml-1 hover:text-red-500">&times;</button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label>Subject *</Label>
            <Input
              value={emailData.subject}
              onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Dispatch Note..."
              data-testid="input-dispatch-email-subject"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              value={emailData.body}
              onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
              placeholder="Enter your message..."
              className="h-32"
            />
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
            <Checkbox
              id="attach-pdf-planner"
              checked={emailData.attach_pdf}
              onCheckedChange={(v) => setEmailData(prev => ({ ...prev, attach_pdf: v }))}
            />
            <label htmlFor="attach-pdf-planner" className="text-sm flex items-center gap-2 cursor-pointer">
              {meta.icon}
              Attach {meta.label} PDF
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSend} disabled={sending || !emailData.to_email || !emailData.subject} data-testid="btn-send-dispatch-email">
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Send Email</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
